import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  ValidationErrors,
  Validators, FormsModule, ReactiveFormsModule
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';

import { RegisterFormStepComponent } from '../../components/register/register-form-step/register-form-step.component';
import { ProgressBarComponent } from '../../components/progress-bar/progress-bar.component';
import {Router, RouterLink} from '@angular/router';
import { RegistrationService } from '../../services/registration/registration.service';
import { ToastrService } from 'ngx-toastr';
import { Tokens } from '../../entities/authentication/tokens.entity';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import {User, UserRoles, UserStatus} from '../../entities/user/user.entity';
import {LogoComponent} from '../../components/logo/logo.component';
import {AnalyticsService} from '../../services/analytics/analytics.service';
import {MetaPixelService} from '../../services/analytics/meta-pixel.service';
import {NgClass} from '@angular/common';
import {LocalStorageService} from '../../services/localstorage/local-storage.service';
import {LanguageService} from '../../services/language/language.service';

/** Validator to ensure passwords match */
export const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password = control.get('password')?.value;
  const confirmPassword = control.get('passwordConfirm')?.value;
  return password === confirmPassword ? null : { passwordMismatch: true };
};

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  imports: [
    TranslocoPipe,
    FormsModule,
    RegisterFormStepComponent,
    ReactiveFormsModule,
    ProgressBarComponent,
    RouterLink,
    LogoComponent,
    NgClass
  ],
  styleUrl: './register.component.scss'
})
export class RegisterComponent implements OnInit {
  public currentStep = 1;
  public submitting = false;
  public static readonly localStorageKey = 'registrationForm';
  public registerForm: FormGroup;
  public currentUser: User|null = null;
  public nextLoading = false;

  constructor(
    private readonly registrationService: RegistrationService,
    private readonly authenticationService: AuthenticationService,
    private readonly fb: FormBuilder,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    private readonly analytics: AnalyticsService,
    private readonly metaPixel: MetaPixelService,
    private readonly localStorage: LocalStorageService,
    private readonly languageService: LanguageService,
  ) {
    this.registerForm = this.fb.group(
      {
        firstname: ['', Validators.required],
        lastname: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(6)]],
        passwordConfirm: ['', [Validators.required, Validators.minLength(6)]],
        selectedPlan: ['pro', Validators.required],
        role: [UserRoles.photographer, Validators.required],
      },
      { validators: passwordMatchValidator }
    );
  }

  ngOnInit(): void {
    this.loadFormFromLocalStorage();
    this.determineCurrentStep();
  }

  /** Getters for form values */
  private get firstname(): string {
    return this.registerForm.get('firstname')?.value;
  }
  private get lastname(): string {
    return this.registerForm.get('lastname')?.value;
  }
  private get email(): string {
    return this.registerForm.get('email')?.value;
  }
  private get password(): string {
    return this.registerForm.get('password')?.value;
  }
  private get role(): string {
    return this.registerForm.get('role')?.value;
  }

  private get plan(): string {
    return this.registerForm.get('selectedPlan')?.value;
  }

  /** Saves non-sensitive form data to localStorage */
  private saveFormToLocalStorage(): void {
    const { password, passwordConfirm, ...safeData } = this.registerForm.value;
    this.localStorage.save(RegisterComponent.localStorageKey, safeData);
  }

  /** Loads form data from localStorage */
  private loadFormFromLocalStorage(): void {
    const savedForm = this.localStorage.get(RegisterComponent.localStorageKey);
    if (savedForm) {
      this.registerForm.patchValue(savedForm);
    }
  }

  /** Clears localStorage */
  private clearFormFromLocalStorage(): void {
    this.localStorage.remove(RegisterComponent.localStorageKey);
  }

  /** Determines which step the user should start from */
  private determineCurrentStep(): void {
    this.currentUser = this.authenticationService.getCurrentUser();
    if (this.currentUser !== null) {
      if (this.currentUser.isActive) {
        this.router.navigate(['/manage/dashboard']);
        return;
      }
      if (this.currentUser.status !== UserStatus.registered) {
        this.router.navigate(['/register/success']);
        return;
      }

      this.router.navigate(['/register/success']);
      return;
    }

    if (!this.firstname || !this.lastname) {
      this.currentStep = 1;
      return;
    }

    if (!this.role) {
      this.currentStep = 2;
      return;
    }

    if (!this.email || !this.password) {
      this.currentStep = 3;
      return;
    }
  }

  /** Advances to the next step */
  public nextStep(): void {
    if (this.currentStep === 3 && this.registerForm.hasError('passwordMismatch')) {
      return; // Prevent progression if passwords don't match
    }

    if (this.currentStep === 1 || this.currentStep === 2) {
      this.currentStep++;
      this.saveFormToLocalStorage();
      return;
    }

    this.analytics.event('onboarding_step_' + this.currentStep);

    // Get the local timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (this.currentStep === 3) {
      if (this.authenticationService.getCurrentUser() !== null) {
        if (this.role === UserRoles.customer) {
          this.clearFormFromLocalStorage();
          this.router.navigate(['/']);
          return;
        }
        this.router.navigate(['/register/success']);
        return;
      }
      this.submitting = true;
      this.nextLoading = true;
      const preferredLanguage =
        this.languageService.getPersistedPreferredLanguage() ?? this.languageService.getWebsiteLanguage();
      this.registrationService.register(
        this.email,
        this.password,
        this.role,
        timezone,
        this.firstname,
        this.lastname,
        preferredLanguage
      ).subscribe({
          next: (tokens: Tokens) => {
            this.authenticationService.saveTokens(tokens);
            this.currentUser = this.authenticationService.getCurrentUser();
            this.analytics.event('onboarding_account_created');
            this.metaPixel.trackRegistration({
              user: this.currentUser,
              email: this.email,
              role: this.role,
              plan: this.plan,
              source: 'onboarding',
            });

            this.clearFormFromLocalStorage();
            if (this.role === UserRoles.photographer) {
              this.router.navigate(['/register/success']);
            } else {
              this.router.navigate(['/']);
            }
            return;
          },
          error: () => {
            this.toastr.error('Please try again. If it fails again, contact support', 'An error occurred');
            this.nextLoading = false;
          },
          complete: () => {
            this.submitting = false;
            this.nextLoading = false;
          }
        });
    }
  }

  /**
   * @TODO: when removing this, make sure to add the check of isPlatformBrowser
  public subscribe(): void {
    if (!this.currentUser) {
      this.toastr.error('Please try again. If it fails again, contact support', 'An error occurred');
      return;
    }

    this.analytics.event('onboarding_subscription_started', { plan: this.plan });

    this.nextLoading = true;

    if (this.currentUser.canGetFreeTrial) {
      this.subscriptionService.subscribeWithFreeTrial(this.plan).subscribe(
        () => {
          this.router.navigate(['register/success']);
          this.nextLoading = false;
        }, (error) => {
          this.toastr.error('Please try again. If it fails again, contact support', 'An error occurred');
          this.nextLoading = false;
        }
      );
    } else {
      this.subscriptionService.subscribe(this.plan).subscribe(
        (result) => {
          window.location.href = result.url;
          this.nextLoading = false;
        }, (error) => {
          this.toastr.error('Please try again. If it fails again, contact support', 'An error occurred');
          this.nextLoading = false;
        }
      );
    }
  }
   **/

  /** Goes back a step */
  public prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;

      if (this.currentStep === 3) {
        // Set a fake password visually
        this.registerForm.get('password')?.setValue('test1234');
        this.registerForm.get('passwordConfirm')?.setValue('test1234');
      }
    }
  }

  public canGoBack(): boolean {
    return this.currentStep > 1 && this.currentUser === null;
  }

  /** Updates the selected plan */
  public selectPlan(plan: string): void {
    this.registerForm.get('selectedPlan')?.setValue(plan);
  }

  public selectRole(role: string): void {
    this.registerForm.get('role')?.setValue(role);
    this.nextStep();
  }

  protected readonly UserRoles = UserRoles;
}
